"use client";

import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
	useCallback,
	useMemo,
} from "react";
import { createLogAdapter } from "@/lib/logger/console-adapter";

interface BeforeInstallPromptEvent extends Event {
	prompt: () => Promise<void>;
	userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

declare global {
	interface Window {
		__odinInstallState?: {
			installStartedAt: number | null;
			promptAcceptedAt: number | null;
			promptDismissedAt: number | null;
			appInstalledEventAt: number | null;
			standaloneLaunchAt: number | null;
			lastOutcome: "accepted" | "dismissed" | null;
			verificationFailedAt: number | null;
		};
	}
}

type InstallChannel =
	| "native-prompt"
	| "ios-manual"
	| "android-manual"
	| "in-app-browser"
	| "generic-manual"
	| "unsupported";

interface PWAContextType {
	isStandalone: boolean;
	isIOS: boolean;
	isAndroid: boolean;
	isMobile: boolean;
	isPageLoaded: boolean;
	isInAppBrowser: boolean;
	canInstall: boolean;
	isNativeInstallReady: boolean;
	canShowInstallGuidance: boolean;
	installChannel: InstallChannel;
	install: () => void;
	showIOSInstructions: boolean;
	setShowIOSInstructions: (show: boolean) => void;
	showAndroidInstructions: boolean;
	setShowAndroidInstructions: (show: boolean) => void;
	showAndroidChecking: boolean;
	setShowAndroidChecking: (show: boolean) => void;
	showAndroidPostInstallHelp: boolean;
	setShowAndroidPostInstallHelp: (show: boolean) => void;
	showGenericInstructions: boolean;
	setShowGenericInstructions: (show: boolean) => void;
	showInAppWarning: boolean;
	setShowInAppWarning: (show: boolean) => void;
	showPageLoadWaiting: boolean;
	setShowPageLoadWaiting: (show: boolean) => void;
	dismissInstallPrompt: () => void;
}

const PWAContext = createContext<PWAContextType | undefined>(undefined);
const uiLog = createLogAdapter("contexts/pwa-context.tsx");
const ANDROID_MANUAL_FALLBACK_MIN_DELAY_MS = 30 * 1000;
const ANDROID_INSTALL_VERIFICATION_DELAY_MS = 25 * 1000;

const updateInstallState = (
	state: Partial<NonNullable<Window["__odinInstallState"]>>,
) => {
	if (typeof window === "undefined") return;
	window.__odinInstallState = {
		installStartedAt: null,
		promptAcceptedAt: null,
		promptDismissedAt: null,
		appInstalledEventAt: null,
		standaloneLaunchAt: null,
		lastOutcome: null,
		verificationFailedAt: null,
		...window.__odinInstallState,
		...state,
	};
	window.dispatchEvent(new CustomEvent("odin:install-state"));
};

export function PWAProvider({ children }: { children: ReactNode }) {
	const [deferredPrompt, setDeferredPrompt] =
		useState<BeforeInstallPromptEvent | null>(null);
	const [isStandalone, setIsStandalone] = useState(false);
	const [isIOS, setIsIOS] = useState(false);
	const [isAndroid, setIsAndroid] = useState(false);
	const [isMobile, setIsMobile] = useState(false);
	const [isPageLoaded, setIsPageLoaded] = useState(false);
	const [isInAppBrowser, setIsInAppBrowser] = useState(false);
	const [showIOSInstructions, setShowIOSInstructions] = useState(false);
	const [showAndroidInstructions, setShowAndroidInstructions] = useState(false);
	const [showAndroidChecking, setShowAndroidChecking] = useState(false);
	const [showAndroidPostInstallHelp, setShowAndroidPostInstallHelp] =
		useState(false);
	const [showGenericInstructions, setShowGenericInstructions] = useState(false);
	const [showInAppWarning, setShowInAppWarning] = useState(false);
	const [showPageLoadWaiting, setShowPageLoadWaiting] = useState(false);
	const [hasUserInteraction, setHasUserInteraction] = useState(false);
	const [androidFallbackDelayReached, setAndroidFallbackDelayReached] =
		useState(false);
	const [installStateVersion, setInstallStateVersion] = useState(0);

	const canFallbackToAndroidManual = isAndroid
		? hasUserInteraction && androidFallbackDelayReached
		: true;

	const closeAllPrompts = useCallback(() => {
		setShowIOSInstructions(false);
		setShowAndroidInstructions(false);
		setShowAndroidChecking(false);
		setShowAndroidPostInstallHelp(false);
		setShowGenericInstructions(false);
		setShowInAppWarning(false);
		setShowPageLoadWaiting(false);
	}, []);

	const openPromptByChannel = useCallback(
		(channel: InstallChannel) => {
			closeAllPrompts();
			if (channel === "ios-manual") {
				setShowIOSInstructions(true);
				return;
			}
			if (channel === "in-app-browser") {
				setShowInAppWarning(true);
				return;
			}
			if (channel === "android-manual") {
				setShowAndroidInstructions(true);
				return;
			}
			if (channel === "generic-manual") {
				setShowGenericInstructions(true);
			}
		},
		[closeAllPrompts],
	);

	const logInstallDiagnostics = useCallback(async (reason: string) => {
		if (typeof window === "undefined") return;

		try {
			const registration = await navigator.serviceWorker?.getRegistration();
			uiLog.info("PWA install diagnostics", {
				reason,
				displayModeStandalone:
					window.matchMedia("(display-mode: standalone)").matches,
				documentReferrer: document.referrer,
				currentPath: window.location.pathname,
				registrationScope: registration?.scope ?? null,
				registrationActiveScriptURL:
					registration?.active?.scriptURL ??
					registration?.waiting?.scriptURL ??
					registration?.installing?.scriptURL ??
					null,
				hasDeferredPrompt: !!deferredPrompt,
			});
		} catch (error) {
			uiLog.warn("Failed to collect PWA install diagnostics", error);
		}
	}, [deferredPrompt]);

	useEffect(() => {
		updateInstallState({
			installStartedAt: null,
			promptAcceptedAt: null,
			promptDismissedAt: null,
			appInstalledEventAt: null,
			standaloneLaunchAt: null,
			lastOutcome: null,
			verificationFailedAt: null,
		});

		// Check if already in standalone mode
		const checkStandalone = () => {
			const isStandaloneMode =
				window.matchMedia("(display-mode: standalone)").matches ||
				(window.navigator as any).standalone ||
				document.referrer.includes("android-app://");

			setIsStandalone(isStandaloneMode);
		};

		checkStandalone();
		window
			.matchMedia("(display-mode: standalone)")
			.addEventListener("change", checkStandalone);
		window.addEventListener("appinstalled", checkStandalone);

		const onLoadComplete = () => {
			setIsPageLoaded(true);
		};
		if (document.readyState === "complete") {
			onLoadComplete();
		} else {
			window.addEventListener("load", onLoadComplete, { once: true });
		}

		// Detect User Agent
		const userAgent = window.navigator.userAgent.toLowerCase();
		const isIpadOSDesktop =
			navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
		const isIosDevice = /iphone|ipad|ipod/.test(userAgent) || isIpadOSDesktop;
		const isAndroidDevice = /android/.test(userAgent);
		const isMobileDevice =
			/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
				userAgent,
			) || navigator.maxTouchPoints > 1;

		setIsIOS(isIosDevice);
		setIsAndroid(isAndroidDevice);
		setIsMobile(isMobileDevice);

		// Detect In-App Browser
		const inAppKeywords = [
			"webview",
			"wv",
			"micromessenger", // WeChat
			"okapp", // OKX
			"tokenpocket",
			"binance",
			"unisat",
			"alipay",
			"dingtalk",
			"qq/",
			"fban", // Facebook
			"fbav", // Facebook
		];
		const isBuiltIn = inAppKeywords.some((keyword) =>
			userAgent.includes(keyword),
		);
		setIsInAppBrowser(isBuiltIn);

		// Handle beforeinstallprompt for Android/Desktop
		const handleBeforeInstallPrompt = (e: Event) => {
			e.preventDefault();
			setDeferredPrompt(e as BeforeInstallPromptEvent);
			uiLog.info("beforeinstallprompt captured");
			void logInstallDiagnostics("beforeinstallprompt");
		};

		window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
		const handleEarlyPromptReady = () => {
			const fromEarlyCapture = (window as any).__odinBIP as
				| BeforeInstallPromptEvent
				| null
				| undefined;
			if (fromEarlyCapture) {
				setDeferredPrompt(fromEarlyCapture);
				uiLog.info("beforeinstallprompt restored from early capture");
			}
		};
		window.addEventListener("odin:beforeinstallprompt-ready", handleEarlyPromptReady);

		const capturedEarly = (window as any).__odinBIP as
			| BeforeInstallPromptEvent
			| null
			| undefined;
		if (capturedEarly) {
			setDeferredPrompt(capturedEarly);
			void logInstallDiagnostics("early-beforeinstallprompt");
		}

		const handleAppInstalled = () => {
			uiLog.info("appinstalled fired");
			updateInstallState({
				appInstalledEventAt: Date.now(),
			});
			void logInstallDiagnostics("appinstalled");
		};
		window.addEventListener("appinstalled", handleAppInstalled);

		const markInteraction = () => {
			setHasUserInteraction(true);
		};
		window.addEventListener("pointerdown", markInteraction, { once: true });
		window.addEventListener("keydown", markInteraction, { once: true });
		window.addEventListener("touchstart", markInteraction, {
			once: true,
			passive: true,
		});
		window.addEventListener("scroll", markInteraction, {
			once: true,
			passive: true,
		});
		const handleInstallStateChange = () => {
			setInstallStateVersion((version) => version + 1);
		};
		window.addEventListener("odin:install-state", handleInstallStateChange);

		return () => {
			window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
			window.removeEventListener(
				"odin:beforeinstallprompt-ready",
				handleEarlyPromptReady,
			);
			window.removeEventListener("pointerdown", markInteraction);
			window.removeEventListener("keydown", markInteraction);
			window.removeEventListener("touchstart", markInteraction);
			window.removeEventListener("scroll", markInteraction);
			window.removeEventListener("odin:install-state", handleInstallStateChange);
			window.removeEventListener("appinstalled", checkStandalone);
			window.removeEventListener("appinstalled", handleAppInstalled);
			window.removeEventListener("load", onLoadComplete);
			window
				.matchMedia("(display-mode: standalone)")
				.removeEventListener("change", checkStandalone);
		};
	}, []);

	useEffect(() => {
		if (!isStandalone || typeof window === "undefined") return;

		const installState = window.__odinInstallState;
		if (!installState) return;
		if (installState.standaloneLaunchAt) return;
		if (
			!installState.promptAcceptedAt &&
			!installState.appInstalledEventAt &&
			!document.referrer.includes("android-app://")
		) {
			return;
		}

		updateInstallState({
			standaloneLaunchAt: Date.now(),
			verificationFailedAt: null,
		});
		closeAllPrompts();
		uiLog.info("Standalone launch detected after install");
	}, [closeAllPrompts, installStateVersion, isStandalone]);

	useEffect(() => {
		if (!isAndroid || isStandalone) return;
		const timer = window.setTimeout(() => {
			setAndroidFallbackDelayReached(true);
			uiLog.info("Android manual fallback delay reached");
		}, ANDROID_MANUAL_FALLBACK_MIN_DELAY_MS);
		return () => window.clearTimeout(timer);
	}, [installStateVersion, isAndroid, isStandalone]);

	useEffect(() => {
		if (
			typeof window === "undefined" ||
			!isAndroid ||
			isStandalone ||
			!window.__odinInstallState?.promptAcceptedAt
		) {
			return;
		}

		const timer = window.setTimeout(() => {
			if (
				window.matchMedia("(display-mode: standalone)").matches ||
				(window.navigator as any).standalone ||
				document.referrer.includes("android-app://") ||
				window.__odinInstallState?.standaloneLaunchAt
			) {
				return;
			}

			updateInstallState({
				verificationFailedAt: Date.now(),
			});
			setShowAndroidChecking(false);
			setShowAndroidPostInstallHelp(true);
			uiLog.warn("Android install not verified after waiting window");
		}, ANDROID_INSTALL_VERIFICATION_DELAY_MS);

		return () => window.clearTimeout(timer);
	}, [isAndroid, isStandalone]);

	const isNativeInstallReady = !!deferredPrompt;
	const canShowInstallGuidance =
		!isStandalone && !isInAppBrowser && (isIOS || isAndroid || isMobile);

	const installChannel = useMemo<InstallChannel>(() => {
		if (isStandalone) return "unsupported";
		if (isInAppBrowser) return "in-app-browser";
		if (isIOS) return "ios-manual";
		if (deferredPrompt) return "native-prompt";
		if (isAndroid) return "android-manual";
		if (isMobile) return "generic-manual";
		return "unsupported";
	}, [deferredPrompt, isAndroid, isIOS, isInAppBrowser, isMobile, isStandalone]);

	const install = useCallback(() => {
		updateInstallState({
			installStartedAt: Date.now(),
			promptAcceptedAt: null,
			promptDismissedAt: null,
			appInstalledEventAt: null,
			standaloneLaunchAt: null,
			lastOutcome: null,
			verificationFailedAt: null,
		});
		void logInstallDiagnostics("install-click");

		if (isStandalone) {
			uiLog.info("Already in standalone mode");
			return;
		}

		if (!isPageLoaded) {
			closeAllPrompts();
			setShowPageLoadWaiting(true);
			uiLog.info("Install blocked: page not fully loaded");
			return;
		}

		if (isInAppBrowser) {
			setShowInAppWarning(true);
			return;
		}

		if (isIOS) {
			setShowIOSInstructions(true);
			return;
		}

		if (deferredPrompt) {
			setShowAndroidChecking(isAndroid);
			setShowAndroidPostInstallHelp(false);
			deferredPrompt.prompt().catch((error) => {
				uiLog.warn("Native install prompt failed, fallback to manual", error);
				setShowAndroidChecking(false);
				openPromptByChannel(isAndroid ? "android-manual" : "generic-manual");
			});
			deferredPrompt.userChoice.then((choiceResult) => {
				updateInstallState({
					lastOutcome: choiceResult.outcome,
					promptAcceptedAt:
						choiceResult.outcome === "accepted" ? Date.now() : null,
					promptDismissedAt:
						choiceResult.outcome === "dismissed" ? Date.now() : null,
				});
				void logInstallDiagnostics(`userChoice:${choiceResult.outcome}`);
				if (choiceResult.outcome === "accepted") {
					uiLog.info("User accepted the install prompt");
					setDeferredPrompt(null);
					if (!isAndroid) {
						closeAllPrompts();
					}
				} else {
					setShowAndroidChecking(false);
					uiLog.info("User dismissed the install prompt");
				}
			});
		} else {
			if (isAndroid && !canFallbackToAndroidManual) {
				setShowAndroidChecking(true);
				uiLog.info("Android install still checking (waiting for delay/interaction)");
				return;
			}
			openPromptByChannel(installChannel);
			uiLog.info(`Fallback manual install guidance: ${installChannel}`);
		}
	}, [
		canFallbackToAndroidManual,
		closeAllPrompts,
		deferredPrompt,
		isPageLoaded,
		installChannel,
		isAndroid,
		isIOS,
		isInAppBrowser,
		isStandalone,
		openPromptByChannel,
	]);

	useEffect(() => {
		if (!showAndroidChecking || !isAndroid || isStandalone) return;
		if (deferredPrompt) {
			setShowAndroidChecking(false);
			setShowAndroidInstructions(true);
			return;
		}
		if (canFallbackToAndroidManual) {
			setShowAndroidChecking(false);
			setShowAndroidInstructions(true);
		}
	}, [
		canFallbackToAndroidManual,
		deferredPrompt,
		isAndroid,
		isStandalone,
		showAndroidChecking,
	]);

	const dismissInstallPrompt = useCallback(() => {
		closeAllPrompts();
	}, [closeAllPrompts]);

	return (
		<PWAContext.Provider
			value={{
				isStandalone,
				isIOS,
				isAndroid,
				isMobile,
				isPageLoaded,
				isInAppBrowser,
				canInstall: isNativeInstallReady,
				isNativeInstallReady,
				canShowInstallGuidance,
				installChannel,
				install,
				showIOSInstructions,
				setShowIOSInstructions,
				showAndroidInstructions,
				setShowAndroidInstructions,
				showAndroidChecking,
				setShowAndroidChecking,
				showAndroidPostInstallHelp,
				setShowAndroidPostInstallHelp,
				showGenericInstructions,
				setShowGenericInstructions,
				showInAppWarning,
				setShowInAppWarning,
				showPageLoadWaiting,
				setShowPageLoadWaiting,
				dismissInstallPrompt,
			}}
		>
			{children}
		</PWAContext.Provider>
	);
}

export function usePWA() {
	const context = useContext(PWAContext);
	if (context === undefined) {
		throw new Error("usePWA must be used within a PWAProvider");
	}
	return context;
}
